// src/components/sponsor/auth/SponsorApply.jsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { applySponsor, resetApplicationState } from "@/store/slices/sponsorSlice";
import {
  Briefcase, Building2, Mail, Phone, FileText,
  Award, Clock, Compass, CheckCircle, AlertCircle
} from "lucide-react";
import ParticleBackground from "@/components/home/ParticleBackground";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";
import SelectDropdown from "@/components/common/SelectDropdown";

const ZONES = [
  { value: "north", label: "North" },
  { value: "south", label: "South" },
  { value: "east", label: "East" },
  { value: "west", label: "West" },
  { value: "northeast", label: "Northeast" },
];

const CATEGORIES = [
  { value: "hydration", label: "Hydration" },
  { value: "medals", label: "Medals" },
  { value: "nutrition", label: "Nutrition" },
  { value: "apparels", label: "Apparels" },
  { value: "gears", label: "Gears" },
  { value: "spares", label: "Spares" },
  { value: "accessories", label: "Accessories" },
  { value: "registration_fees", label: "Registration Fees" },
  { value: "insurance", label: "Insurance" },
  { value: "bikes", label: "Bikes" },
  { value: "certificates", label: "Certificates" },
  { value: "route_signages", label: "Route Signages" },
  { value: "branding", label: "Branding" },
  { value: "finance_and_support_vehicles", label: "Finance & Support Vehicles" },
];

const SPONSORSHIP_TYPES = [
  { value: "funded", label: "Funded" },
  { value: "kind", label: "In-Kind" },
  { value: "funded_and_kind", label: "Funded & In-Kind" },
];

const DURATION_OPTIONS = [
  { value: "event", label: "Per Event" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const SponsorApply = () => {
  const dispatch = useDispatch();
  const { isLoading, error, applicationSubmitted } = useSelector((state) => state.sponsor);
  const [categories, setCategories] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      company_name: "",
      applicant_email: "",
      applicant_phone: "",
      gst_number: "",
      zones: [],
      sponsorship_type: "",
      duration_interest: "",
    },
  });

  React.useEffect(() => {
    return () => {
      dispatch(resetApplicationState());
    };
  }, [dispatch]);

  const handleToggleCategory = (value) => {
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const onSubmit = async (data) => {
    if (categories.length === 0) return;

    const applicationData = {
      ...data,
      categories,
      gst_number: data.gst_number.toUpperCase(),
    };

    dispatch(applySponsor(applicationData));
  };

  if (applicationSubmitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center py-12 px-4 overflow-hidden">
        <ParticleBackground />
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative z-10 max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 text-center"
        >
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            Application Submitted!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Thank you for your interest in sponsoring cycling events on Koset!
            Our team will review your application and contact you within 3-5 business days.
            Once approved, you'll receive your login credentials via email.
          </p>
          <Link
            to="/sponsor/login"
            className="inline-flex items-center text-teal-600 hover:text-teal-700 dark:text-teal-400 font-semibold"
          >
            Already approved? Login here →
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative py-12 px-4 overflow-hidden">
      <ParticleBackground />

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 md:p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Become a Sponsor
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Partner with us to sponsor cycling events
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Company Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              Company Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Company Name *"
                placeholder="Enter company name"
                error={errors.company_name?.message}
                {...register("company_name", {
                  required: "Company name is required",
                  minLength: { value: 2, message: "Must be at least 2 characters" },
                  maxLength: { value: 100, message: "Cannot exceed 100 characters" },
                })}
              />
              <TextInput
                label="GST Number *"
                icon={<FileText className="w-5 h-5" />}
                placeholder="e.g., 29ABCDE1234F1Z5"
                error={errors.gst_number?.message}
                {...register("gst_number", {
                  required: "GST number is required",
                  pattern: {
                    value: /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
                    message: "Please provide a valid GST number",
                  },
                })}
              />
            </div>
          </section>

          {/* Contact Details */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-600" />
              Contact Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                label="Email Address *"
                type="email"
                icon={<Mail className="w-5 h-5" />}
                placeholder="contact@company.com"
                error={errors.applicant_email?.message}
                {...register("applicant_email", {
                  required: "Email is required",
                  pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email address" },
                })}
              />
              <TextInput
                label="Phone Number *"
                icon={<Phone className="w-5 h-5" />}
                placeholder="9876543210"
                error={errors.applicant_phone?.message}
                {...register("applicant_phone", {
                  required: "Phone number is required",
                  pattern: { value: /^[6-9]\d{9}$/, message: "Please provide a valid 10-digit Indian phone number" },
                })}
              />
            </div>
          </section>

          {/* Business Categories */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-teal-600" />
              Business Categories
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.value}
                  className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    categories.includes(cat.value)
                      ? "border-teal-500 bg-teal-50 dark:bg-teal-900/30 dark:border-teal-600"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={categories.includes(cat.value)}
                    onChange={() => handleToggleCategory(cat.value)}
                    className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{cat.label}</span>
                </label>
              ))}
            </div>
            {categories.length === 0 && (
              <p className="text-sm text-red-500 mt-2">At least one category is required</p>
            )}
          </section>

          {/* Zones of Interest */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Compass className="w-5 h-5 text-teal-600" />
              Zones of Interest
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ZONES.map((zone) => (
                <label
                  key={zone.value}
                  className="flex items-center gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    value={zone.value}
                    {...register("zones", {
                      validate: (value) => value.length >= 1 || "Select at least one zone",
                    })}
                    className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{zone.label}</span>
                </label>
              ))}
            </div>
            {errors.zones && (
              <p className="text-sm text-red-500 mt-1">{errors.zones.message}</p>
            )}
          </section>

          {/* Sponsorship Preferences */}
          <section>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-teal-600" />
              Sponsorship Preferences
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="sponsorship_type"
                control={control}
                rules={{ required: "Sponsorship type is required" }}
                render={({ field }) => (
                  <SelectDropdown
                    label="Sponsorship Type *"
                    options={SPONSORSHIP_TYPES}
                    placeholder="Select sponsorship type"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={errors.sponsorship_type?.message}
                  />
                )}
              />
              <Controller
                name="duration_interest"
                control={control}
                rules={{ required: "Duration interest is required" }}
                render={({ field }) => (
                  <SelectDropdown
                    label="Duration of Interest *"
                    options={DURATION_OPTIONS}
                    placeholder="Select duration"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={errors.duration_interest?.message}
                  />
                )}
              />
            </div>
          </section>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </motion.div>
          )}

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={categories.length === 0}
              className="flex-1 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 disabled:opacity-50"
              size="lg"
            >
              Submit Application
            </Button>
            <Link
              to="/sponsor/login"
              className="flex-1 py-3 text-center border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Already have an account?
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default SponsorApply;
